// src/pages/Terms.jsx — PhsarGame Terms & Rules
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.legal-pg { background:#121212; color:#fff; font-family:'Inter',sans-serif; min-height:100vh; overflow-x:hidden; }

/* ── HERO BANNER ── */
.legal-hero {
  background:#0d0d0d; border-bottom:1px solid rgba(255,255,255,0.06);
  padding:120px 80px 56px; position:relative; overflow:hidden;
}
.legal-hero-grid {
  position:absolute; inset:0; z-index:0;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size:48px 48px;
}
.legal-hero-glow {
  position:absolute; z-index:1; top:-30%; left:10%; transform:translateX(-50%);
  width:600px; height:400px;
  background:radial-gradient(ellipse, rgba(0,120,242,0.08) 0%, transparent 70%);
  pointer-events:none;
}
.legal-hero-inner { max-width:900px; margin:0 auto; position:relative; z-index:2; }
.legal-tag {
  display:inline-flex; align-items:center; gap:8px;
  background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25);
  border-radius:4px; padding:5px 12px; margin-bottom:20px;
  font-size:11px; font-weight:700; color:#0078F2; letter-spacing:1.2px; text-transform:uppercase;
}
.legal-hero h1 {
  font-size:clamp(32px,4.5vw,56px); font-weight:900; letter-spacing:-1.5px;
  color:#fff; margin-bottom:16px; line-height:1.08;
}
.legal-hero h1 span { color:#0078F2; }
.legal-hero-sub { font-size:15px; color:#6b6b6b; line-height:1.65; max-width:560px; }
.legal-hero-meta {
  display:flex; align-items:center; gap:20px; margin-top:28px; flex-wrap:wrap;
}
.legal-meta-item { font-size:12px; color:#4a4a4a; display:flex; align-items:center; gap:6px; }
.legal-meta-dot { width:4px; height:4px; background:#0078F2; border-radius:50%; }

/* ── LAYOUT ── */
.legal-layout {
  max-width:1100px; margin:0 auto; padding:60px 80px 100px;
  display:grid; grid-template-columns:220px 1fr; gap:56px; align-items:start;
}

/* Sticky TOC */
.legal-toc { position:sticky; top:84px; }
.legal-toc-title { font-size:11px; font-weight:700; color:#3a3a3a; text-transform:uppercase; letter-spacing:1.2px; margin-bottom:16px; }
.legal-toc-list { list-style:none; display:flex; flex-direction:column; gap:2px; }
.legal-toc-item a {
  display:block; font-size:13px; font-weight:500; color:#6b6b6b;
  text-decoration:none; padding:7px 12px; border-radius:6px;
  border-left:2px solid transparent; transition:all 0.15s;
}
.legal-toc-item a:hover { color:#fff; background:rgba(255,255,255,0.04); border-left-color:#0078F2; }

/* ── CONTENT ── */
.legal-content { min-width:0; }
.legal-section { margin-bottom:52px; scroll-margin-top:90px; }
.legal-section-num {
  font-size:11px; font-weight:700; color:#0078F2; text-transform:uppercase;
  letter-spacing:1.2px; margin-bottom:10px;
}
.legal-section h2 {
  font-size:22px; font-weight:800; color:#fff; letter-spacing:-0.4px;
  margin-bottom:18px; line-height:1.2;
  padding-bottom:14px; border-bottom:1px solid rgba(255,255,255,0.06);
}
.legal-section p {
  font-size:14px; color:#8a8a8a; line-height:1.8; margin-bottom:14px;
}
.legal-section p:last-child { margin-bottom:0; }
.legal-section ul {
  list-style:none; display:flex; flex-direction:column; gap:10px; margin-bottom:14px;
}
.legal-section ul li {
  font-size:14px; color:#8a8a8a; line-height:1.75;
  padding-left:20px; position:relative;
}
.legal-section ul li::before {
  content:''; position:absolute; left:0; top:9px;
  width:5px; height:5px; background:#0078F2; border-radius:50%;
}
.legal-highlight {
  background:rgba(0,120,242,0.07); border:1px solid rgba(0,120,242,0.15);
  border-left:3px solid #0078F2; border-radius:0 6px 6px 0;
  padding:14px 18px; margin:16px 0;
  font-size:13px; color:#a3c4f8; line-height:1.7;
}

/* ── CONTACT CARD ── */
.legal-contact {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); border-radius:10px;
  padding:28px; margin-top:56px; display:flex; align-items:center;
  justify-content:space-between; gap:24px; flex-wrap:wrap;
}
.legal-contact-left h3 { font-size:16px; font-weight:700; color:#fff; margin-bottom:6px; }
.legal-contact-left p { font-size:13px; color:#6b6b6b; }
.legal-contact-btn {
  font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  color:#fff; background:#0078F2; border:none; border-radius:6px;
  padding:10px 22px; cursor:pointer; text-decoration:none; transition:all 0.15s; white-space:nowrap;
}
.legal-contact-btn:hover { background:#0a84ff; }

@media(max-width:900px) {
  .legal-hero { padding:100px 40px 44px; }
  .legal-layout { grid-template-columns:1fr; padding:40px 40px 80px; gap:32px; }
  .legal-toc { display:none; }
}
@media(max-width:580px) {
  .legal-hero { padding:90px 20px 36px; }
  .legal-layout { padding:32px 20px 64px; }
}
`;

const CONTENT = {
  en: {
    tag: "Legal Document",
    title: "Terms &",
    accent: "Rules",
    sub: "Please read these terms carefully before using PhsarGame. By accessing or purchasing from our store, you agree to be bound by these terms.",
    updated: "Last updated: January 2025",
    effective: "Effective immediately",
    toc: [
      { id:"acceptance", label:"Acceptance of Terms" },
      { id:"services",   label:"Our Services" },
      { id:"accounts",   label:"User Accounts" },
      { id:"purchases",  label:"Purchases & Payment" },
      { id:"delivery",   label:"Digital Delivery" },
      { id:"refunds",    label:"Refund Policy" },
      { id:"conduct",    label:"User Conduct" },
      { id:"ip",         label:"Intellectual Property" },
      { id:"liability",  label:"Limitation of Liability" },
      { id:"changes",    label:"Changes to Terms" },
    ],
    sections: [
      {
        id:"acceptance", num:"01", title:"Acceptance of Terms",
        body: [
          { type:"p", text:"By accessing and using PhsarGame ('the Store', 'we', 'us', or 'our'), you accept and agree to be bound by these Terms and Rules and our Privacy Policy. If you do not agree to these terms, please do not use our services." },
          { type:"p", text:"These terms apply to all visitors, users, and customers of PhsarGame. We reserve the right to update these terms at any time, and continued use of the service constitutes acceptance of any modifications." },
          { type:"highlight", text:"⚠️ You must be at least 13 years old to use PhsarGame. If you are under 18, you must have parental consent." },
        ]
      },
      {
        id:"services", num:"02", title:"Our Services",
        body: [
          { type:"p", text:"PhsarGame is a digital game store based in Cambodia that provides:" },
          { type:"ul", items:[
            "Digital game accounts and licenses for PC gaming platforms (Steam, Epic Games, etc.)",
            "Game top-up credits and in-game currency",
            "Game account packages and bundles",
            "Customer support via Telegram and live chat",
          ]},
          { type:"p", text:"All products are digital goods. No physical items will be shipped. Availability of specific titles is subject to stock and may change without notice." },
        ]
      },
      {
        id:"accounts", num:"03", title:"User Accounts",
        body: [
          { type:"p", text:"To make a purchase, you must create an account with a valid email address. You are responsible for maintaining the security of your account and password." },
          { type:"ul", items:[
            "You may not share your account with others or transfer it to another person",
            "You must provide accurate and complete registration information",
            "You are responsible for all activities that occur under your account",
            "Notify us immediately of any unauthorized account access",
          ]},
          { type:"p", text:"We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or abuse our services." },
        ]
      },
      {
        id:"purchases", num:"04", title:"Purchases & Payment",
        body: [
          { type:"p", text:"All prices on PhsarGame are listed in USD. Payment is processed securely via ABA Pay (QR code). By completing a purchase, you confirm that you are authorized to use the payment method provided." },
          { type:"ul", items:[
            "Orders are confirmed only after successful payment verification",
            "We reserve the right to cancel orders that appear fraudulent",
            "Prices may change at any time without prior notice",
            "All sales are final unless covered by our Refund Policy",
          ]},
          { type:"highlight", text:"💳 We currently accept payment via ABA Pay QR code only. Ensure your payment is made within the 10-minute QR expiry window." },
        ]
      },
      {
        id:"delivery", num:"05", title:"Digital Delivery",
        body: [
          { type:"p", text:"After successful payment, your game account or key will be delivered to your PhsarGame order page and/or via Telegram within the timeframe stated at checkout." },
          { type:"ul", items:[
            "Instant delivery for in-stock items (typically under 5 minutes)",
            "Manual delivery for some items may take up to 24 hours",
            "Check your Orders page or Telegram for delivery confirmation",
            "Do not share delivered account credentials with others",
          ]},
          { type:"p", text:"Once a digital product is delivered and accessed, it is considered fulfilled. Keep your credentials safe — we cannot recover accounts lost due to user negligence." },
        ]
      },
      {
        id:"refunds", num:"06", title:"Refund Policy",
        body: [
          { type:"p", text:"Due to the nature of digital goods, all sales are generally final. However, we will issue a refund or replacement in the following cases:" },
          { type:"ul", items:[
            "The delivered account or key does not work as described",
            "A duplicate order was placed due to a system error",
            "The product was significantly different from its description",
            "We are unable to fulfill your order within 48 hours",
          ]},
          { type:"highlight", text:"🔄 To request a refund, contact us within 24 hours of delivery via Telegram @phsargame. Refund requests made after this window may not be honored." },
          { type:"p", text:"We do not offer refunds for change of mind, accidental purchases after the product has been accessed, or if you purchased the wrong region/platform version." },
        ]
      },
      {
        id:"conduct", num:"07", title:"User Conduct",
        body: [
          { type:"p", text:"When using PhsarGame, you agree not to:" },
          { type:"ul", items:[
            "Use the service for any unlawful or fraudulent purpose",
            "Attempt to hack, reverse-engineer, or disrupt the platform",
            "Resell purchased accounts or keys without prior written permission",
            "Provide false information or impersonate another person",
            "Abuse the chat or support system with spam or harassment",
          ]},
          { type:"p", text:"Violation of these conduct rules may result in immediate account suspension and legal action where applicable under Cambodian law." },
        ]
      },
      {
        id:"ip", num:"08", title:"Intellectual Property",
        body: [
          { type:"p", text:"All content on PhsarGame — including logos, graphics, text, and UI design — is the property of PhsarGame or its content suppliers and protected by applicable intellectual property laws." },
          { type:"p", text:"Game titles, covers, and trademarks belong to their respective publishers and developers. PhsarGame is an independent retailer and is not affiliated with Steam, Epic Games, or any game publisher unless explicitly stated." },
        ]
      },
      {
        id:"liability", num:"09", title:"Limitation of Liability",
        body: [
          { type:"p", text:"PhsarGame is not liable for any indirect, incidental, or consequential damages arising from your use of our services. Our total liability to you for any claim shall not exceed the amount paid for the specific transaction in question." },
          { type:"p", text:"We are not responsible for losses resulting from unauthorized access to your account due to your failure to maintain password security, or for issues caused by third-party platforms (Steam, Epic Games, etc.)." },
        ]
      },
      {
        id:"changes", num:"10", title:"Changes to Terms",
        body: [
          { type:"p", text:"We may revise these Terms and Rules at any time. When we do, we will update the 'Last updated' date at the top of this page. We encourage you to review these terms periodically." },
          { type:"p", text:"For questions about these terms, contact us at: Telegram @phsargame or via our Facebook page." },
        ]
      },
    ],
    contactTitle: "Have questions about our terms?",
    contactSub: "Our team is happy to clarify anything. Reach out anytime.",
    contactBtn: "Contact Us",
  },

  km: {
    tag: "ឯកសារច្បាប់",
    title: "លក្ខខណ្ឌ និង",
    accent: "ច្បាប់",
    sub: "សូមអានលក្ខខណ្ឌទាំងនេះដោយយកចិត្តទុកដាក់មុនពេលប្រើ PhsarGame។ ដោយការចូលប្រើ ឬទិញពីហាងរបស់យើង អ្នកយល់ព្រមចងភ្ជាប់ដោយលក្ខខណ្ឌទាំងនេះ។",
    updated: "ធ្វើបច្ចុប្បន្នភាពចុងក្រោយ៖ មករា ២០២៥",
    effective: "មានប្រសិទ្ធភាពភ្លាមៗ",
    toc: [
      { id:"acceptance", label:"ការទទួលស្គាល់លក្ខខណ្ឌ" },
      { id:"services",   label:"សេវាកម្មរបស់យើង" },
      { id:"accounts",   label:"គណនីអ្នកប្រើ" },
      { id:"purchases",  label:"ការទិញ និងការបង់ប្រាក់" },
      { id:"delivery",   label:"ការដឹកជញ្ជូនឌីជីថល" },
      { id:"refunds",    label:"គោលការណ៍សងប្រាក់វិញ" },
      { id:"conduct",    label:"ការប្រព្រឹត្តរបស់អ្នកប្រើ" },
      { id:"ip",         label:"កម្មសិទ្ធិបញ្ញា" },
      { id:"liability",  label:"ការកំណត់ទំនួលខុសត្រូវ" },
      { id:"changes",    label:"ការផ្លាស់ប្ដូរលក្ខខណ្ឌ" },
    ],
    sections: [
      {
        id:"acceptance", num:"០១", title:"ការទទួលស្គាល់លក្ខខណ្ឌ",
        body: [
          { type:"p", text:"ដោយការចូលប្រើ និងប្រើប្រាស់ PhsarGame ('ហាង', 'យើង', 'ពួកយើង') អ្នកទទួលយក និងយល់ព្រមចងភ្ជាប់ដោយលក្ខខណ្ឌ និងច្បាប់ទាំងនេះ ព្រមទាំងគោលការណ៍ភាពឯកជនរបស់យើង។ ប្រសិនបើអ្នកមិនយល់ព្រមទាំងនេះ សូមមកកុំប្រើសេវាកម្មរបស់យើង។" },
          { type:"p", text:"លក្ខខណ្ឌទាំងនេះអនុវត្តចំពោះអ្នកទស្សនា អ្នកប្រើប្រាស់ និងអតិថិជនទាំងអស់នៃ PhsarGame។ យើងរក្សាសិទ្ធិក្នុងការធ្វើបច្ចុប្បន្នភាពលក្ខខណ្ឌទាំងនេះគ្រប់ពេល ហើយការបន្តប្រើប្រាស់សេវាកម្មបង្ហាញពីការទទួលយករាល់ការផ្លាស់ប្ដូរ។" },
          { type:"highlight", text:"⚠️ អ្នកត្រូវមានអាយុយ៉ាងហោចណាស់ ១៣ ឆ្នាំ ដើម្បីប្រើ PhsarGame។ ប្រសិនបើអ្នកក្រោម ១៨ ឆ្នាំ អ្នកត្រូវមានការអនុញ្ញាតពីឪពុកម្ដាយ។" },
        ]
      },
      {
        id:"services", num:"០២", title:"សេវាកម្មរបស់យើង",
        body: [
          { type:"p", text:"PhsarGame គឺជាហាងហ្គេមឌីជីថលដែលមានមូលដ្ឋាននៅកម្ពុជា ដែលផ្ដល់ជូន:" },
          { type:"ul", items:[
            "គណនីហ្គេមឌីជីថល និងអាជ្ញាបណ្ណសម្រាប់វេទិកាហ្គេម PC (Steam, Epic Games ជាដើម)",
            "ក្រេឌីតបន្ថែមហ្គេម និងរូបិយប័ណ្ណក្នុងហ្គេម",
            "កញ្ចប់គណនីហ្គេម និងផ្តុំ",
            "ជំនួយអតិថិជនតាម Telegram និងជជែកផ្ទាល់",
          ]},
          { type:"p", text:"ផលិតផលទាំងអស់ជាទំនិញឌីជីថល។ មិនមានការដឹកជញ្ជូនវត្ថុរូបវន្តទេ។ ការចូលប្រើចំណងជើងជាក់លាក់ស្ថិតនៅក្រោមការគ្រប់គ្រងស្ដុក ហើយអាចផ្លាស់ប្ដូរដោយគ្មានការជូនដំណឹងទុកជាមុន។" },
        ]
      },
      {
        id:"accounts", num:"០៣", title:"គណនីអ្នកប្រើ",
        body: [
          { type:"p", text:"ដើម្បីធ្វើការទិញ អ្នកត្រូវបង្កើតគណនីដោយប្រើអ៊ីមែលត្រឹមត្រូវ។ អ្នកទទួលខុសត្រូវក្នុងការរក្សាសុវត្ថិភាពគណនី និងពាក្យសម្ងាត់របស់អ្នក។" },
          { type:"ul", items:[
            "អ្នកមិនអាចចែករំលែកគណនីជាមួយអ្នកដទៃ ឬផ្ទេរវាទៅម្នាក់ផ្សេងទៀតបានទេ",
            "អ្នកត្រូវផ្ដល់ព័ត៌មានចុះឈ្មោះត្រឹមត្រូវ និងពេញលេញ",
            "អ្នកទទួលខុសត្រូវចំពោះសកម្មភាពទាំងអស់ដែលកើតឡើងក្នុងគណនីរបស់អ្នក",
            "ជូនដំណឹងយើងភ្លាមៗប្រសិនបើមានការចូលប្រើគណនីដោយគ្មានការអនុញ្ញាត",
          ]},
          { type:"p", text:"យើងរក្សាសិទ្ធិក្នុងការផ្អាក ឬបញ្ចប់គណនីដែលល្មើសលក្ខខណ្ឌទាំងនេះ ចូលរួមក្នុងសកម្មភាពបោកប្រាស់ ឬរំខានសេវាកម្មរបស់យើង។" },
        ]
      },
      {
        id:"purchases", num:"០៤", title:"ការទិញ និងការបង់ប្រាក់",
        body: [
          { type:"p", text:"តម្លៃទាំងអស់នៅ PhsarGame ត្រូវបានបង្ហាញជា USD។ ការទូទាត់ត្រូវបានដំណើរការដោយសុវត្ថិភាពតាម ABA Pay (QR code)។ ដោយការបញ្ចប់ការទិញ អ្នកបញ្ជាក់ថាអ្នកមានសិទ្ធិប្រើវិធីទូទាត់ដែលបានផ្ដល់ជូន។" },
          { type:"ul", items:[
            "ការបញ្ជាទិញត្រូវបានបញ្ជាក់តែបន្ទាប់ពីការផ្ទៀងផ្ទាត់ការបង់ប្រាក់ដោយជោគជ័យ",
            "យើងរក្សាសិទ្ធិក្នុងការលុបចោលការបញ្ជាទិញដែលហាក់ដូចជាបោកប្រាស់",
            "តម្លៃអាចផ្លាស់ប្ដូរគ្រប់ពេលដោយគ្មានការជូនដំណឹងទុកជាមុន",
            "ការលក់ទាំងអស់ជារឿងចប់សព្វបែបហើយ លុះត្រាតែស្ថិតក្រោមគោលការណ៍សងប្រាក់វិញ",
          ]},
          { type:"highlight", text:"💳 យើងទទួលការបង់ប្រាក់តាម ABA Pay QR code តែប៉ុណ្ណោះ។ ត្រូវប្រាកដថាការបង់ប្រាក់ត្រូវបានធ្វើក្នុងរយៈពេល ១០ នាទីនៃការផុតសុពលភាព QR។" },
        ]
      },
      {
        id:"delivery", num:"០៥", title:"ការដឹកជញ្ជូនឌីជីថល",
        body: [
          { type:"p", text:"បន្ទាប់ពីការបង់ប្រាក់ជោគជ័យ គណនីហ្គេម ឬ key របស់អ្នកនឹងត្រូវបានដឹកជញ្ជូនទៅទំព័របញ្ជាទិញ PhsarGame របស់អ្នក និង/ឬតាម Telegram ក្នុងរយៈពេលដែលបានបញ្ជាក់នៅពេលចុះឈ្មោះ។" },
          { type:"ul", items:[
            "ការដឹកជញ្ជូនភ្លាមៗសម្រាប់ទំនិញក្នុងស្ដុក (ជាធម្មតាក្រោម ៥ នាទី)",
            "ការដឹកជញ្ជូនដោយដៃសម្រាប់ទំនិញខ្លះអាចចំណាយពេលរហូតដល់ ២៤ ម៉ោង",
            "ពិនិត្យទំព័របញ្ជាទិញ ឬ Telegram របស់អ្នកដើម្បីបញ្ជាក់ការដឹកជញ្ជូន",
            "កុំចែករំលែកព័ត៌មានសម្ងាត់គណនីដែលបានដឹកជញ្ជូនជាមួយអ្នកដទៃ",
          ]},
          { type:"p", text:"នៅពេលដែលផលិតផលឌីជីថលត្រូវបានដឹកជញ្ជូន និងចូលប្រើ វាត្រូវបានចាត់ទុកថាបានបំពេញ។ រក្សាព័ត៌មានសម្ងាត់របស់អ្នកឱ្យបានសុវត្ថិភាព — យើងមិនអាចស្ដារគណនីដែលបាត់ដោយសារការធ្វេសប្រហែសរបស់អ្នកប្រើបានទេ។" },
        ]
      },
      {
        id:"refunds", num:"០៦", title:"គោលការណ៍សងប្រាក់វិញ",
        body: [
          { type:"p", text:"ដោយសារលក្ខណៈនៃទំនិញឌីជីថល ការលក់ទាំងអស់ជាធម្មតាជារឿងចប់សព្វបែប។ ទោះជាយ៉ាងណា យើងនឹងផ្ដល់ការសងប្រាក់វិញ ឬការជំនួសក្នុងករណីខាងក្រោម:" },
          { type:"ul", items:[
            "គណនី ឬ key ដែលបានដឹកជញ្ជូនមិនដំណើរការដូចការពណ៌នាទេ",
            "ការបញ្ជាទិញស្ទួនត្រូវបានធ្វើឡើងដោយសារកំហុសប្រព័ន្ធ",
            "ផលិតផលខុសប្លែកយ៉ាងខ្លាំងពីការពណ៌នារបស់វា",
            "យើងមិនអាចបំពេញការបញ្ជាទិញរបស់អ្នកក្នុងរយៈពេល ៤៨ ម៉ោងបានទេ",
          ]},
          { type:"highlight", text:"🔄 ដើម្បីស្នើសុំការសងប្រាក់វិញ ទូទាក់យើងក្នុងរយៈពេល ២៤ ម៉ោងបន្ទាប់ពីការដឹកជញ្ជូន តាម Telegram @phsargame។ ការស្នើសុំសងប្រាក់វិញក្រោយពេលនេះប្រហែលជាមិនត្រូវបានពិចារណាទេ។" },
          { type:"p", text:"យើងមិនផ្ដល់ការសងប្រាក់វិញសម្រាប់ការប្ដូរចិត្ត ការទិញដោយចៃដន្យបន្ទាប់ពីការចូលប្រើផលិតផល ឬប្រសិនបើអ្នកទិញកំណែតំបន់/វេទិកាខុស។" },
        ]
      },
      {
        id:"conduct", num:"០៧", title:"ការប្រព្រឹត្តរបស់អ្នកប្រើ",
        body: [
          { type:"p", text:"នៅពេលប្រើ PhsarGame អ្នកយល់ព្រមមិន:" },
          { type:"ul", items:[
            "ប្រើសេវាកម្មសម្រាប់គោលបំណងខុសច្បាប់ ឬបោកប្រាស់",
            "ព្យាយាមហេគ វិស្វករវិញ ឬរំខានវេទិក",
            "លក់ឡើងវិញគណនី ឬ key ដែលបានទិញដោយគ្មានការអនុញ្ញាតជាលាយលក្ខណ៍អក្សរ",
            "ផ្ដល់ព័ត៌មានមិនពិត ឬក្លែងបន្លំជាម្នាក់ផ្សេង",
            "រំខានប្រព័ន្ធជជែក ឬជំនួយដោយ spam ឬការបៀតបៀន",
          ]},
          { type:"p", text:"ការបំពានច្បាប់ប្រព្រឹត្តទាំងនេះអាចនឹងបណ្ដាលឱ្យផ្អាកគណនីភ្លាមៗ និងការចាត់វិធានការផ្លូវច្បាប់តាមច្បាប់កម្ពុជា។" },
        ]
      },
      {
        id:"ip", num:"០៨", title:"កម្មសិទ្ធិបញ្ញា",
        body: [
          { type:"p", text:"មាតិកាទាំងអស់នៅ PhsarGame — រួមមានឡូហ្គោ រូបភាព អត្ថបទ និងការរចនា UI — គឺជាទ្រព្យសម្បត្តិរបស់ PhsarGame ឬអ្នកផ្ដល់មាតិការបស់ខ្លួន ហើយត្រូវបានការពារដោយច្បាប់កម្មសិទ្ធិបញ្ញា។" },
          { type:"p", text:"ចំណងជើងហ្គេម គម្របឆ្ករ និងស្លាកសញ្ញាពាណិជ្ជកម្មជាកម្មសិទ្ធិរបស់អ្នកបោះពុម្ព និងអ្នកបង្កើតរៀងៗខ្លួន។ PhsarGame គឺជាអ្នកលក់ឯករាជ្យ និងមិនមានទំនាក់ទំនងជាមួយ Steam, Epic Games ឬអ្នកបោះពុម្ពហ្គេមមួយណាទេ លុះត្រាតែបញ្ជាក់ច្បាស់។" },
        ]
      },
      {
        id:"liability", num:"០៩", title:"ការកំណត់ទំនួលខុសត្រូវ",
        body: [
          { type:"p", text:"PhsarGame មិនទទួលខុសត្រូវចំពោះការខូចខាតដោយប្រយោល ការខូចខាតចៃដន្យ ឬការខូចខាតជាផលវិបាកណាមួយដែលកើតចេញពីការប្រើប្រាស់សេវាកម្មរបស់យើង។ ទំនួលខុសត្រូវសរុបរបស់យើងចំពោះអ្នកសម្រាប់ការទាមទារណាមួយនឹងមិនលើសពីចំនួនដែលបានបង់សម្រាប់ប្រតិបត្តិការជាក់លាក់ទេ។" },
          { type:"p", text:"យើងមិនទទួលខុសត្រូវចំពោះការខាតបង់ដែលបណ្ដាលមកពីការចូលប្រើគណនីរបស់អ្នកដោយគ្មានការអនុញ្ញាតដោយសារការបរាជ័យក្នុងការរក្សាសុវត្ថិភាពពាក្យសម្ងាត់ ឬបញ្ហាដែលបណ្ដាលមកពីវេទិកាភាគីទីបី (Steam, Epic Games ជាដើម)។" },
        ]
      },
      {
        id:"changes", num:"១០", title:"ការផ្លាស់ប្ដូរលក្ខខណ្ឌ",
        body: [
          { type:"p", text:"យើងអាចកែប្រែលក្ខខណ្ឌ និងច្បាប់ទាំងនេះគ្រប់ពេល។ នៅពេលដែលយើងធ្វើ យើងនឹងធ្វើបច្ចុប្បន្នភាពកាលបរិច្ឆេទ 'ធ្វើបច្ចុប្បន្នភាពចុងក្រោយ' នៅដើមទំព័រនេះ។ យើងលើកទឹកចិត្តអ្នកឱ្យពិនិត្យលក្ខខណ្ឌទាំងនេះជាទៀងទាត់។" },
          { type:"p", text:"សម្រាប់សំណួរអំពីលក្ខខណ្ឌទាំងនេះ ទូទាក់យើងតាម Telegram @phsargame ឬតាមទំព័រ Facebook របស់យើង។" },
        ]
      },
    ],
    contactTitle: "មានសំណួរអំពីលក្ខខណ្ឌរបស់យើង?",
    contactSub: "ក្រុមរបស់យើងសប្បាយចិត្តក្នុងការបំភ្លឺ។ ទំនាក់ទំនងគ្រប់ពេល។",
    contactBtn: "ទូទាក់យើង",
  }
};

export default function Terms() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;

  function renderBody(items) {
    return items.map((item, i) => {
      if (item.type === "p") return <p key={i}>{item.text}</p>;
      if (item.type === "highlight") return <div key={i} className="legal-highlight">{item.text}</div>;
      if (item.type === "ul") return (
        <ul key={i}>
          {item.items.map((li, j) => <li key={j}>{li}</li>)}
        </ul>
      );
      return null;
    });
  }

  return (
    <>
      <style>{S}</style>
      <div className="legal-pg">
        <Navbar />

        {/* Hero */}
        <div className="legal-hero">
          <div className="legal-hero-grid" />
          <div className="legal-hero-glow" />
          <div className="legal-hero-inner">
            <div className="legal-tag">📜 {c.tag}</div>
            <h1>{c.title} <span>{c.accent}</span></h1>
            <p className="legal-hero-sub">{c.sub}</p>
            <div className="legal-hero-meta">
              <span className="legal-meta-item"><span className="legal-meta-dot" />{c.updated}</span>
              <span className="legal-meta-item"><span className="legal-meta-dot" />{c.effective}</span>
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="legal-layout">
          {/* TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-title">Contents</div>
            <ul className="legal-toc-list">
              {c.toc.map(item => (
                <li key={item.id} className="legal-toc-item">
                  <a href={`#${item.id}`}>{item.label}</a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Content */}
          <div className="legal-content">
            {c.sections.map(sec => (
              <div key={sec.id} id={sec.id} className="legal-section">
                <div className="legal-section-num">{sec.num}</div>
                <h2>{sec.title}</h2>
                {renderBody(sec.body)}
              </div>
            ))}

            {/* Contact card */}
            <div className="legal-contact">
              <div className="legal-contact-left">
                <h3>{c.contactTitle}</h3>
                <p>{c.contactSub}</p>
              </div>
              <a href="https://t.me/phsargame" target="_blank" rel="noreferrer" className="legal-contact-btn">
                {c.contactBtn}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}